import type { ConnectorDefinition } from "../types";

/**
 * Notion MCP tools that only READ. Always available once the connector is live;
 * never gated by the write flag. Names are the bare MCP tool names Notion's
 * hosted server advertises (see
 * https://developers.notion.com/guides/mcp/mcp-supported-tools). The `notion-`
 * prefix is part of the advertised name; the AI SDK bridge namespaces them
 * again under `notion_*` for the model.
 */
export const NOTION_READ_TOOLS = [
  "notion-search",
  "notion-fetch",
  "notion-query-data-sources",
  "notion-query-database-view",
  "notion-query-meeting-notes",
  "notion-get-comments",
  "notion-get-teams",
  "notion-get-users",
  "notion-get-async-task",
] as const;

/**
 * Notion MCP tools that WRITE / mutate workspace content. Gated behind
 * `connectors.notion.write`: with the write flag off they never materialize for
 * the model (filtered in `defineMcpCapability.resolveTools`), so the connector
 * ships read-only first. Imported by the Notion capability so the connector
 * definition stays the single source of truth for the read/write split.
 *
 * The same list drives HITL: the capability declares an approval for every tool
 * here (ADR-050), so a write the user hasn't trusted prompts before it runs.
 * Adding a name here without a label there is a compile error.
 */
export const NOTION_WRITE_TOOLS = [
  "notion-create-pages",
  "notion-update-page",
  "notion-move-pages",
  "notion-duplicate-page",
  "notion-create-database",
  "notion-update-data-source",
  "notion-create-view",
  "notion-update-view",
  "notion-create-comment",
] as const;

/**
 * Notion connector — OAuth 2.1-backed MCP.
 *
 * Unlike GitHub/monday (where a vendor IdP issues the bearer and a separate
 * hosted MCP server consumes it), Notion's MCP server at `https://mcp.notion.com`
 * IS its own OAuth 2.1 authorization server (`/authorize`, `/token`, PKCE S256
 * required, refresh-token grant). There is no dashboard to register a static
 * OAuth app — credentials come from a one-time Dynamic Client Registration
 * (RFC 7591) baked into `NOTION_MCP_CLIENT_ID` / `NOTION_MCP_CLIENT_SECRET` via
 * `pnpm run notion:register-mcp-client`. The resulting per-user access token is
 * injected as `Authorization: Bearer` into the same MCP server.
 *
 * Token lifecycle worth designing around (see the shared refresh path in
 * `service.ts`): access tokens last ~8h (we honor `expires_in`), and EVERY
 * refresh rotates the refresh token — replaying a rotated-out token revokes the
 * whole grant. The shared refresh path serializes refreshes with a Redis lock
 * and treats `invalid_grant` as terminal (status `error`, user must reconnect).
 *
 * `usePKCE` is on because Notion's authorization server requires it. `scopes` is
 * empty: Notion MCP defines no OAuth scopes, and `getAuthorizationUrl` omits the
 * `scope` param entirely when the list is empty.
 *
 * No `validateAccess` probe is needed: the access token is issued by the MCP
 * server's own auth server, so a successful token exchange already proves the
 * server will accept it — and Notion's REST `GET /v1/users/me` deliberately
 * rejects MCP-audienced tokens, so a REST probe would fail a valid connection.
 */

/**
 * DECISION RECORD — client identity: DCR (chosen) vs. CIMD (deferred)
 * Discussed 2026-07. Recorded here so the tradeoff isn't relitigated blind.
 *
 * There are TWO independent expiry clocks, and it's important not to conflate
 * them:
 *
 * 1. The per-user GRANT (their access + refresh tokens). Notion's auth server
 *    caps every grant at whichever comes first:
 *      - 180 days absolute from first authorization (does NOT slide — refreshing
 *        never extends it), or
 *      - 30 consecutive days of inactivity (no successful refresh).
 *    When a grant dies we get `invalid_grant` (handled as terminal in
 *    service.ts) and the user must click "Connect Notion" again. This is a
 *    property of the token, set by Notion, and is IDENTICAL regardless of how we
 *    identify our client. No client design avoids it. See Notion's MCP client
 *    guide: https://developers.notion.com/guides/mcp/build-mcp-client
 *
 * 2. The DCR CLIENT REGISTRATION itself (the NOTION_MCP_CLIENT_ID/SECRET this
 *    script mints). Notion can expire the registered client, and publishes NO
 *    TTL — third parties report it "can happen after a period," and our own
 *    registration came back with no `client_secret_expires_at`. When a client
 *    expires you can no longer start NEW authorizations with it, but tokens it
 *    already issued keep working and keep refreshing — *provided you refresh them
 *    with the client_id that issued them*.
 *
 * The gap in THIS implementation: we store a single global client_id/secret per
 * environment and `oauth.ts#refreshAccessToken` refreshes with whatever is
 * currently in the env var — not the client_id that issued each grant. So if the
 * client expires and we re-register (to let new users connect), overwriting the
 * env var with a NEW client_id, every currently-connected user's next refresh
 * hits a credential mismatch → `invalid_grant` → they must reconnect. In short:
 * with today's design, rotating the client forces all live users to reconnect.
 *
 * The alternative — CIMD (Client ID Metadata Document, RFC draft + MCP SEP-991,
 * "preferred default" as of the 2025-11-25 MCP spec; Notion's docs say the MCP
 * server supports it, but only in one passing sentence). The client_id becomes a
 * stable HTTPS URL we host that serves a small JSON doc (client_id matching the
 * URL, client_name, redirect_uris, grant_types, token_endpoint_auth_method:
 * "none"). No secret (PKCE only), no expiry, no rotation. One doc can list every
 * env's callback, so a single client_id is shared across local/dev/prod. That
 * makes the global-client-id refresh path correct by construction, eliminating
 * the forced-reconnect-on-rotation problem above. Costs/caveats: host a public,
 * unauthenticated HTTPS endpoint; the client_id URL must be https (localhost
 * can't self-host, so local points at the shared dev/prod-hosted doc that lists
 * the localhost callback); Notion caches the doc up to ~24h; and Notion's CIMD
 * support is effectively undocumented, so it must be verified end-to-end against
 * mcp.notion.com before trusting it.
 *
 * WHY WE STAYED ON DCR (for now): this ships flag-gated to a small initial user
 * base. The client-expiry risk is theoretical (no published TTL), NON-SILENT
 * (the symptom is "new connections fail at authorize" — existing users keep
 * working until we act), and recoverable. Switching to CIMD is an upfront,
 * partially-unverified cost (new public endpoint + public-client support in
 * oauth.ts + the localhost-https wrinkle + verifying Notion honors a URL
 * client_id). At current scope that math favors DCR. Revisit CIMD if this goes
 * broad GA (a single client expiry then forces EVERYONE to reconnect at once) or
 * if expiry becomes a recurring annoyance.
 *
 * RUNBOOK — if NEW Notion connections start failing at the authorize step, the
 * DCR client likely expired. Re-run:
 *     pnpm run notion:register-mcp-client --env <env> --force
 * This mints a fresh client and overwrites the two secrets. Currently-connected
 * users will have to reconnect once (see the gap above); already-issued grants
 * that haven't yet needed a refresh are unaffected until they do. If forced
 * reconnects become unacceptable, migrate to CIMD (above) or track the issuing
 * client_id per connection and refresh with the stored pair.
 */
export const notionConnector: ConnectorDefinition = {
  id: "notion",
  name: "Notion",
  description:
    "Read and update your Notion pages, databases, comments, and meeting notes",
  icon: "notion",
  authType: "oauth",
  kind: "mcp",
  oauth: {
    authorizationUrl: "https://mcp.notion.com/authorize",
    tokenUrl: "https://mcp.notion.com/token",
    scopes: [],
    usePKCE: true,
    clientIdEnvVar: "NOTION_MCP_CLIENT_ID",
    clientSecretEnvVar: "NOTION_MCP_CLIENT_SECRET",
  },
  mcp: {
    serverUrl: "https://mcp.notion.com/mcp",
    transport: "http",
    toolAllowlist: [...NOTION_READ_TOOLS, ...NOTION_WRITE_TOOLS],
    writeToolNames: [...NOTION_WRITE_TOOLS],
  },
  isEnabled: () =>
    Boolean(
      process.env.NOTION_MCP_CLIENT_ID && process.env.NOTION_MCP_CLIENT_SECRET
    ),
};
